import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, useWindowDimensions, ViewProps } from 'react-native';
import format from 'date-fns/format';
import sortBy from 'lodash/sortBy';
import { DataProvider, LayoutProvider, RecyclerListView, RecyclerListViewProps } from "recyclerlistview";
import { Hub, HubType } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNAButton,
  DNACard,
  DNAText,
  DNAHeader,
  DNADivider,
  GridList,
  util,
  Iffy,
  luxColors,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAChip, GridListSingleColumnHeader, ListFieldConfig } from 'src/components/DNA';
import { useDispatch } from 'src/state/redux';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import { HubVariant, useHubsState } from 'src/state/context/Hubs/HubsStateProvider';
import { useHubTemplatesList } from 'src/state/redux/selector/hub';
import { SortDir } from '../DNA/hooks/useSort';
import HubsContextMenu from './HubsContextMenu';

const S = StyleSheet.create({
  header: {
    borderColor: colors['color-gray-80'],
    borderWidth: 1,
    padding: 0,
    shadowOpacity: 0,
  },
  TableHeader: {
    backgroundColor: colors['color-gray-10'],
    paddingHorizontal: 24,
    paddingVertical: 14,
  },
  SortableButton: {
    minHeight: 17,
    maxHeight: 17,
  },
  Row: {
    justifyContent: 'center',
    flexBasis: 'auto',
    paddingHorizontal: 24,
    paddingVertical: 14,
  },
  desktopContainer: {
    overflow: 'hidden',
    borderRadius: 4,
    flex: 1,
  },
  contextMenuContainer: {
    paddingRight: 12
  },
  itemWrapper: {
    width: '100%'
  },
});

interface HubRowProps {
  height?: number,
  hub: Hub,
}

enum SortOrders {
  'ascending',
  'descending'
}

type SortOrder = keyof typeof SortOrders
type hubsFields = keyof Hub
interface SelectedSort {
  fieldName: hubsFields
  sortOrder: SortOrder
}

interface HubsListConfig extends ListFieldConfig {
  sortable?: boolean
}

const fields: HubsListConfig[] = [
  {
    fieldName: 'name',
    fieldLabel: 'Name',
    fieldType: 'LABEL',
    dataType: 'string',
    defaultSort: SortDir.asc,
    sortable: true,
    width: 2,
  },
  {
    fieldName: 'createdAt',
    fieldLabel: 'Date Created',
    fieldType: 'LABEL',
    dataType: 'date',
    defaultSort: SortDir.asc,
    sortable: true,
    width: 2,
  },
]

const HubRow : React.FC<HubRowProps> = ({ hub }) => {
  const { isOnline, deviceMode } = useAppSettings();
  const { handleEditHub, openPreviewWindow } = useHubsState();
  const { name, createdAt, id } = hub;
  const isTablet = deviceMode === 'tablet';

  const onPreview = () => openPreviewWindow(id)

  const hasActivePermissions = hub.sharedPermissions?.some(permission => !permission.isDeleted)

  return (
    <GridList.Row
      style={S.Row}
      interactive="hoverable"
      hoverColor={luxColors.mainContainer.primary}
      onPress={() => handleEditHub(id)}
      testID="hub-item-row"
    >
      {/* Name */}
      <GridList.Col span={1}>
        <DNAText testID="hub-item-row-name" numberOfLines={4}>
          {name}
        </DNAText>
      </GridList.Col>

      {/* Date Created */}
      <GridList.Col span={1}>
        <DNAText testID="hub-item-row-date">
          {format(new Date(createdAt), 'LLL d, y')}
        </DNAText>
      </GridList.Col>

      {/* Context Menu */}
      <GridList.Col span={1}>
        <DNABox fill spacing={isTablet ? undefined : 'xl'} alignX='end' style={S.contextMenuContainer}>
            <DNABox appearance="row" fill alignY='center'>
            <Iffy is={hub.hubType === HubType.TEMPLATE_INSTANCE}>
              <DNAChip.Beacon children="FROM TEMPLATE"/>
            </Iffy>
            <Iffy is={hub.hubType === HubType.TEMPLATE && hasActivePermissions}>
              <DNAChip.Beacon variant="FOLDER_STATUS_SHARED" />
            </Iffy>
          </DNABox>
          <DNABox appearance="row" spacing="sm">
            <DNAButton
              appearance="outline"
              status="tertiary"
              size={isTablet ? 'xl' : 'md'}
              iconRight="pencil-simple"
              onPress={() => handleEditHub(id)}
            />
            <Iffy is={isOnline}>
              <DNAButton
                appearance="outline"
                status="tertiary"
                size={isTablet ? 'xl' : 'md'}
                iconRight="eye"
                onPress={onPreview}
              />
            </Iffy>
            <HubsContextMenu hub={hub} />
          </DNABox>
        </DNABox>
      </GridList.Col>
    </GridList.Row>
  )
}

const HubsTableContent: React.FC<HubsTableProps> = ({ emptyComponent }) => {
  const { deviceMode } = useAppSettings()
  const dimensions = useWindowDimensions();
  
  const { columnConfig } = deviceModeVariants[deviceMode]
  const [selectedSort, setSelectedSort] = useState<SelectedSort>({
    fieldName: 'createdAt',
    sortOrder: 'descending',
  })
  const { hubsORM, variant } = useHubsState()
  const hubs = hubsORM.map(hubORM => hubORM.model).filter(hub => {
    if (variant !== HubVariant.TEMPLATE) {
      return hub.hubType !== HubType.TEMPLATE
    }
    return true
  })

  const sortedData = selectedSort.sortOrder === 'ascending'
    ? sortBy(hubs, selectedSort.fieldName)
    : sortBy(hubs, selectedSort.fieldName).reverse()

  const toggleSort = (fieldName) => {
    const fieldIsActive = selectedSort.fieldName = fieldName
    if (fieldIsActive) {
      setSelectedSort(p => ({ ...p, sortOrder:  selectedSort.sortOrder === 'ascending' ? 'descending' : 'ascending' }))
    } else {
      setSelectedSort({
        fieldName: fieldName,
        sortOrder: 'ascending',
      })
    }
  }

  // RECYCLER LIST //
  const dataProviderInit = useMemo(
    () => (new DataProvider((prev: string, next: string) => prev !== next))
      .cloneWithRows(sortedData),
    [sortedData]
  );

  const layoutProvider = useMemo(
    () => new LayoutProvider(_ => 0,
      (_, dim) => {
        dim.width = 2000;
        dim.height = 58;
      },
    ),
    []
  );

  const rowRenderer = useCallback<RecyclerListViewProps['rowRenderer']>(
    (type, hub: Hub) => {
      return (
        <React.Fragment key={hub.id}>
          <HubRow hub={hub} />
          <DNADivider />
        </React.Fragment>
      );
    },
    []
  )

  const renderItemWrapper = useCallback<NonNullable<RecyclerListViewProps['renderItemContainer']>>(
    (props, parentProps, children) => {
      const { style } = props as { style?: ViewProps };
      return (
        <DNABox {...props} style={{ ...style, ...S.itemWrapper }}>
          {children}
        </DNABox>
      )
    },
    []
  )

  return (
    <DNABox appearance="col" fill>
      {/* Table */}
      <Iffy is={emptyComponent}>
        <DNABox style={{ height: 320 }}>
          {emptyComponent}
        </DNABox>
      </Iffy>
      <Iffy is={!emptyComponent}>
        <GridList
          cols={columnConfig}
          gap="12px"
        >
          <GridList.Header
            style={util.mergeStyles(undefined, S.TableHeader)}
          >
            {
              fields.map(field => {
                const sortDir = selectedSort.fieldName === field.fieldName
                  ? selectedSort.sortOrder === 'ascending'
                    ? 'asc'
                    : 'desc'
                  : 'none'
                return (
                  <GridListSingleColumnHeader
                    key={field.fieldName}
                    style={S.SortableButton}
                    toggleSort={toggleSort}
                    sortDir={sortDir}
                    field={field}
                    testID={`header-sort-${field.fieldName}`}
                  />
                )
              })
            }
          </GridList.Header>
          {/* Hub Rows */}
          <RecyclerListView
            layoutProvider={layoutProvider}
            dataProvider={dataProviderInit}
            rowRenderer={rowRenderer}
            renderAheadOffset={dimensions.height}
            canChangeSize
            forceNonDeterministicRendering
            renderItemContainer={renderItemWrapper}
          />
        </GridList>
      </Iffy>
    </DNABox>
  )
}

const HubsTableTablet: React.FC<HubsTableProps> = ({ emptyComponent }) => {
  return <HubsTableContent emptyComponent={emptyComponent} />
}

const HubsTableDesktop: React.FC<HubsTableProps> = ({ emptyComponent }) => {
  return (
    <DNACard
      appearance="flat"
      style={S.desktopContainer}
    >
      <HubsTableContent emptyComponent={emptyComponent} />
    </DNACard>
  )
}

interface DeviceModeVariant {
  component: React.ElementType,
  columnConfig: string
}

type DeviceModeVariants = Record<DeviceMode, DeviceModeVariant>

const deviceModeVariants: DeviceModeVariants = {
  desktop: {
    component: HubsTableDesktop,
    columnConfig: 'minmax(200px, 100%) minmax(150px, 100%) minmax(300px,100%)',
  },
  tablet: {
    component: HubsTableTablet,
    columnConfig: 'minmax(200px, 100%) minmax(150px, 100%) minmax(300px,100%)',
  },
}

interface HubsTableProps {
  emptyComponent?: React.ReactNode
}

const HubsTable:React.FC<HubsTableProps> = ({ emptyComponent }) => {
  const { deviceMode } = useAppSettings()

  const CurrentDeviceModeVariant = deviceModeVariants[deviceMode].component
  return <CurrentDeviceModeVariant emptyComponent={emptyComponent} />
}

export default HubsTable;
